import { useEffect, useMemo, useState } from 'react';
import styles from './settingsForm.module.css';

/**
 * Paginate a list so it never grows past the panel height. Each page shows
 * `pageSize` items; navigate with `<` / `>` arrows. Used by every settings
 * panel that displays a teacher-grown list (announcements, events,
 * birthdays) so the panel stays under the 1080-stage viewport budget.
 */
export function usePager<T>(items: readonly T[], pageSize: number) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Snap back if removals shrink the list past the current page.
  useEffect(() => {
    if (page >= totalPages) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  const visible = useMemo(
    () => items.slice(page * pageSize, page * pageSize + pageSize),
    [items, page, pageSize],
  );

  return {
    visible,
    page,
    totalPages,
    setPage,
    next: () => setPage((p) => Math.min(totalPages - 1, p + 1)),
    prev: () => setPage((p) => Math.max(0, p - 1)),
  };
}

export interface PagerProps {
  page: number;
  totalPages: number;
  onNext: () => void;
  onPrev: () => void;
  /** Singular noun shown in the page label (e.g. "events"). */
  itemLabel?: string;
}

export function Pager({ page, totalPages, onNext, onPrev, itemLabel = 'items' }: PagerProps) {
  if (totalPages <= 1) return null;
  return (
    <div className={styles.pager} aria-label={`${itemLabel} pagination`}>
      <button
        type="button"
        className={styles.pagerButton}
        onClick={onPrev}
        disabled={page === 0}
        aria-label="Previous page"
      >
        ‹
      </button>
      <span>
        Page {page + 1} of {totalPages}
      </span>
      <button
        type="button"
        className={styles.pagerButton}
        onClick={onNext}
        disabled={page >= totalPages - 1}
        aria-label="Next page"
      >
        ›
      </button>
    </div>
  );
}
